import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Form, InputGroup, Badge, Modal } from "react-bootstrap";
import { Button, Select } from "antd";
import { PlusOutlined, UserAddOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { getuserdata } from "../../../services/apiService";

const parseMetaPayload = (metaValue) => {
    if (!metaValue) {
        return {};
    }
    if (typeof metaValue === "object") {
        return metaValue || {};
    }
    try {
        return JSON.parse(metaValue);
    } catch (error) {
        console.warn("Unable to parse meta payload", error);
        return {};
    }
};

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const recordMatchesInsuranceUser = (record, userData) => {
    if (!record || !userData?.id) {
        return false;
    }

    const userId = toNumericOrNull(userData.id);
    const companyId = toNumericOrNull(userData.company_id || userData.companyId || userData.company);
    const agentId = toNumericOrNull(userData.agent_id || userData.agentId);

    const recordCompanyId = toNumericOrNull(record.company_id || record.companyId);
    const recordAgentId = toNumericOrNull(record.agent_id || record.agentId);

    if (companyId && recordCompanyId && companyId === recordCompanyId) {
        return true;
    }

    if (agentId && recordAgentId && agentId === recordAgentId) {
        return true;
    }

    const directUserCandidates = [
        record.user_id,
        record.userId,
        record.created_by,
        record.createdBy,
        record.owner_id,
        record.ownerId,
        record.agent_user_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    if (directUserCandidates.includes(userId)) {
        return true;
    }

    const meta = parseMetaPayload(record.meta);
    const metaCandidates = [
        meta?.created_by,
        meta?.user_id,
        meta?.owner_id,
        meta?.agent_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    return metaCandidates.includes(userId);
};

const RELATION_OPTIONS = [
    "Self",
    "Spouse",
    "Parent",
    "Child",
    "Sibling",
    "Dependent",
    "Other",
];

const CustomerManagement = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [familyMembers, setFamilyMembers] = useState({}); // { customerId: [familyMembers] }
    const [policies, setPolicies] = useState({}); // { customerId: [policies] }
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [showFamilyModal, setShowFamilyModal] = useState(false);
    const [submittingFamily, setSubmittingFamily] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [familyFormData, setFamilyFormData] = useState({
        customer_id: "",
        name: "",
        email: "",
        phone: "",
        relationship: "",
        date_of_birth: "",
        gender: "",
        address: "",
        notes: "",
    });

    const companyId = useMemo(
        () => toNumericOrNull(userData?.company_id || userData?.companyId || userData?.company),
        [userData]
    );

    const agentId = useMemo(
        () => toNumericOrNull(userData?.agent_id || userData?.agentId),
        [userData]
    );

    useEffect(() => {
        fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id, companyId, agentId]);

    const handleFamilyFormChange = (e) => {
        const { name, value } = e.target;
        setFamilyFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFamilyFormSubmit = async (e) => {
        e.preventDefault();
        
        if (!familyFormData.customer_id) {
            toast.error("Please select a customer");
            return;
        }

        if (!familyFormData.name.trim()) {
            toast.error("Please enter full name");
            return;
        }

        if (!familyFormData.relationship) {
            toast.error("Please select relationship");
            return;
        }

        setSubmittingFamily(true);
        try {
            const userId = await getuserdata("id");
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            
            // Get the selected customer to inherit company_id and agent_id
            const selectedCustomer = customers.find(c => c.id === familyFormData.customer_id);
            const parentCompanyId = selectedCustomer?.company_id || companyId;
            const parentAgentId = selectedCustomer?.agent_id || agentId;
            
            // If still no company_id, try to get a default one
            let finalCompanyId = parentCompanyId;
            if (!finalCompanyId) {
                try {
                    const companyResponse = await oneKlicks.read("", "insurance_companies", {}, "created_at DESC", "", "", "0,1");
                    if (companyResponse?.success && Array.isArray(companyResponse?.data) && companyResponse.data.length > 0) {
                        finalCompanyId = companyResponse.data[0].id;
                    }
                } catch (err) {
                    console.warn("Could not fetch default company:", err);
                }
            }
            
            if (!finalCompanyId) {
                toast.error("Company information not found. Please ensure a company exists.");
                setSubmittingFamily(false);
                return;
            }
            
            const payload = {
                name: familyFormData.name.trim(),
                full_name: familyFormData.name.trim(),
                customer_name: familyFormData.name.trim(),
                email: familyFormData.email.trim() || null,
                phone: familyFormData.phone.trim() || null,
                address: familyFormData.address.trim() || null,
                relationship: familyFormData.relationship,
                date_of_birth: familyFormData.date_of_birth || null,
                gender: familyFormData.gender || null,
                id_proof_type: "Other", // Default value for required field
                id_proof_number: "", // Default empty string
                status: "active",
                user_id: userId,
                company_id: finalCompanyId,
                agent_id: parentAgentId || null,
                parent_customer_id: String(familyFormData.customer_id),
                meta: JSON.stringify({
                    notes: familyFormData.notes?.trim() || null,
                }),
                created_at: timestamp,
                updated_at: timestamp,
            };

            console.log("Creating family member with payload:", payload);
            const response = await oneKlicks.create("", "insurance_customers", payload);
            console.log("Family member creation response:", response);

            if (response?.success) {
                toast.success("Family member added successfully");
                // Close modal immediately
                setShowFamilyModal(false);
                // Reset form
                setFamilyFormData({
                    customer_id: "",
                    name: "",
                    email: "",
                    phone: "",
                    relationship: "",
                    date_of_birth: "",
                    gender: "",
                    address: "",
                    notes: "",
                });
                // Refresh customers list to show new family member
                await fetchCustomers();
            } else {
                toast.error(response?.message || response?.error || "Failed to add family member");
            }
        } catch (error) {
            console.error("Error adding family member:", error);
            toast.error("Failed to add family member");
        } finally {
            setSubmittingFamily(false);
        }
    };

    const fetchFamilyMembers = async (customerIds) => {
        if (!customerIds || customerIds.length === 0) {
            setFamilyMembers({});
            return;
        }

        try {
            // Fetch all customers and filter for family members
            const conditions = {};
            if (companyId) {
                conditions.company_id = companyId;
            }
            if (!conditions.company_id && agentId) {
                conditions.agent_id = agentId;
            }
            if (!conditions.company_id && !conditions.agent_id) {
                conditions.user_id = userData.id;
            }

            const response = await oneKlicks.read(
                "",
                "insurance_customers",
                conditions,
                "id DESC",
                "",
                "",
                "0,500"
            );

            if (response?.success) {
                const rows = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.items)
                        ? response.data.items
                        : [];
                
                const filtered = rows.filter((row) => recordMatchesInsuranceUser(row, userData));
                
                // Filter family members (those with parent_customer_id) and group by parent
                const customerIdSet = new Set(customerIds.map(id => String(id)));
                const grouped = {};
                
                filtered.forEach((member) => {
                    const parentId = member.parent_customer_id;
                    if (parentId && customerIdSet.has(String(parentId))) {
                        if (!grouped[parentId]) {
                            grouped[parentId] = [];
                        }
                        grouped[parentId].push(member);
                    }
                });
                
                setFamilyMembers(grouped);
            } else {
                setFamilyMembers({});
            }
        } catch (error) {
            console.error("Error fetching family members:", error);
            setFamilyMembers({});
        }
    };

    const fetchPoliciesForCustomers = async (customerIds) => {
        if (!customerIds || customerIds.length === 0) {
            setPolicies({});
            return;
        }

        try {
            const conditions = {};
            
            if (companyId) {
                conditions.company_id = companyId;
            }
            if (!conditions.company_id && agentId) {
                conditions.agent_id = agentId;
            }
            if (!conditions.company_id && !conditions.agent_id) {
                conditions.user_id = userData.id;
            }

            const response = await oneKlicks.read(
                "",
                "insurance_policies",
                conditions,
                "created_at DESC",
                "",
                "",
                "0,500"
            );

            if (response?.success) {
                const rows = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.items)
                        ? response.data.items
                        : [];
                
                const filtered = rows.filter((row) => recordMatchesInsuranceUser(row, userData));
                
                // Filter policies for the specific customer IDs and group by customer_id
                const customerIdSet = new Set(customerIds.map(id => String(id)));
                const grouped = {};
                
                filtered.forEach((policy) => {
                    const custId = policy.customer_id;
                    if (custId && customerIdSet.has(String(custId))) {
                        if (!grouped[custId]) {
                            grouped[custId] = [];
                        }
                        grouped[custId].push(policy);
                    }
                });
                
                setPolicies(grouped);
            } else {
                setPolicies({});
            }
        } catch (error) {
            console.error("Error fetching policies:", error);
            setPolicies({});
        }
    };

    const fetchCustomers = async () => {
        if (!userData?.id) {
            setCustomers([]);
            setFamilyMembers({});
            setPolicies({});
            return;
        }

        try {
            setLoadingCustomers(true);
            const conditions = {};
            if (companyId) {
                conditions.company_id = companyId;
            }
            if (!conditions.company_id && agentId) {
                conditions.agent_id = agentId;
            }
            if (!conditions.company_id && !conditions.agent_id) {
                conditions.user_id = userData.id;
            }

            const response = await oneKlicks.read(
                "",
                "insurance_customers",
                conditions,
                "id DESC",
                "",
                "",
                "0,200"
            );

            if (response?.success) {
                const rows = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.items)
                        ? response.data.items
                        : [];
                const filtered = rows.filter((row) => recordMatchesInsuranceUser(row, userData));
                
                // Filter out family members (those with parent_customer_id)
                const mainCustomers = filtered.filter((row) => !row.parent_customer_id);
                setCustomers(mainCustomers);

                // Fetch family members and policies for these customers
                const customerIds = mainCustomers.map((c) => c.id);
                if (customerIds.length > 0) {
                    await Promise.all([
                        fetchFamilyMembers(customerIds),
                        fetchPoliciesForCustomers(customerIds),
                    ]);
                }
            } else {
                setCustomers([]);
                setFamilyMembers({});
                setPolicies({});
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
            toast.error("Unable to load customers");
            setCustomers([]);
            setFamilyMembers({});
            setPolicies({});
        } finally {
            setLoadingCustomers(false);
        }
    };

    const renderLoading = () => (
        <div className="text-center p-5">
            <div className="spinner-border" role="status"></div>
        </div>
    );

    const filteredCustomers = useMemo(() => {
        if (!searchText.trim()) {
            return customers;
        }
        const searchLower = searchText.toLowerCase();
        return customers.filter((customer) => {
            return (
                customer.name?.toLowerCase().includes(searchLower) ||
                customer.full_name?.toLowerCase().includes(searchLower) ||
                customer.customer_name?.toLowerCase().includes(searchLower) ||
                customer.email?.toLowerCase().includes(searchLower) ||
                customer.phone?.toLowerCase().includes(searchLower) ||
                customer.relationship?.toLowerCase().includes(searchLower)
            );
        });
    }, [customers, searchText]);

    const renderEmptyState = (message) => (
        <div className="dc-emptydata-holder">
            <div className="dc-emptydata">
                <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                    <span></span>
                    <em>{message}</em>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <style>
                {`
                    .custom-select .ant-select-selector {
                        border-radius: 5px !important;
                    }
                    .ant-select-dropdown {
                        border-radius: 5px !important;
                    }
                `}
            </style>
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                    <div className="container-fluid py-3">
                    {/* Customer Management Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Customer Management</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Customers</Badge>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button 
                                        className="btn-success" 
                                        onClick={() => setShowFamilyModal(true)}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <UserAddOutlined /> Add Family Member
                                    </Button>
                                    <Button 
                                        className="btn-primary" 
                                        onClick={() => navigate("/insurance/customers/new")}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <PlusOutlined /> Add New Customer
                                    </Button>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginBottom: '20px' }}>
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search customers..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        />
                                    </InputGroup>
                                    </div>
                            </div>
                        </div>
                    </div>

                    {/* Customers Table */}
                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            {loadingCustomers ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredCustomers.length > 0 ? (
                                    <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle">
                                            <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Image</th>
                                                <th>Customer Details</th>
                                                <th>Contact</th>
                                                <th>Family Members</th>
                                                <th>Policies</th>
                                                <th>Status</th>
                                                <th style={{ width: '150px' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {filteredCustomers.map((customer) => (
                                                <tr key={customer.id}>
                                                    <td data-label="ID">{customer.id}</td>
                                                    <td data-label="Image">
                                                        <div style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#1890ff',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '18px'
                                                        }}>
                                                            {(customer.name || customer.full_name || 'C').charAt(0).toUpperCase()}
                                                        </div>
                                                    </td>
                                                    <td data-label="Customer Details">
                                                        <div>
                                                            <div style={{ fontWeight: '500', color: '#333' }}>
                                                                {customer.name || customer.full_name || customer.customer_name || `Customer #${customer.id}`}
                                                            </div>
                                                            {customer.relationship && (
                                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                    {customer.relationship}
                                                                </div>
                                                            )}
                                                            {customer.address && (
                                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                    <i className="ti-location-pin" style={{ marginRight: '4px' }}></i>
                                                                    {customer.address.length > 40 ? `${customer.address.substring(0, 40)}...` : customer.address}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td data-label="Contact">
                                                        <div>
                                                            {customer.email && (
                                                                <div style={{ fontSize: '13px', color: '#333', marginBottom: '4px' }}>
                                                                    <i className="ti-email" style={{ marginRight: '4px', color: '#666' }}></i>
                                                                    {customer.email}
                                                                </div>
                                                            )}
                                                            {customer.phone && (
                                                                <div style={{ fontSize: '13px', color: '#333' }}>
                                                                    <i className="ti-mobile" style={{ marginRight: '4px', color: '#666' }}></i>
                                                                    {customer.phone}
                                                                </div>
                                                            )}
                                                            {!customer.email && !customer.phone && "—"}
                                                        </div>
                                                    </td>
                                                    <td data-label="Family Members">
                                                        {familyMembers[customer.id] && familyMembers[customer.id].length > 0 ? (
                                                            <div>
                                                                {familyMembers[customer.id].map((member, idx) => (
                                                                    <div key={member.id} style={{ marginBottom: idx < familyMembers[customer.id].length - 1 ? '8px' : '0' }}>
                                                                        <div style={{ fontSize: '13px', color: '#333', fontWeight: '500' }}>
                                                                            {member.name || member.full_name || member.customer_name || `Member #${member.id}`}
                                                                            {member.relationship && (
                                                                                <span style={{ fontSize: '12px', color: '#666', marginLeft: '6px', fontWeight: '400' }}>
                                                                                    ({member.relationship})
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {member.phone && (
                                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                                                <i className="ti-mobile" style={{ marginRight: '4px' }}></i>
                                                                                {member.phone}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#999', fontSize: '13px' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td data-label="Policies">
                                                        {policies[customer.id] && policies[customer.id].length > 0 ? (
                                                            <div>
                                                                <Badge bg="success" style={{ marginBottom: '6px', display: 'inline-block', borderRadius: "5px" }}>
                                                                    {policies[customer.id].length} {policies[customer.id].length === 1 ? 'Policy' : 'Policies'}
                                                                </Badge>
                                                                {policies[customer.id].slice(0, 2).map((policy, idx) => (
                                                                    <div key={policy.id} style={{ marginTop: idx > 0 ? '6px' : '0', fontSize: '12px', color: '#666' }}>
                                                                        <i className="ti-file" style={{ marginRight: '4px', color: '#666' }}></i>
                                                                        {policy.policy_number || `POL-${policy.id}`}
                                                                        {policy.product_name && (
                                                                            <div style={{ fontSize: '11px', color: '#999', marginTop: '2px', marginLeft: '16px' }}>
                                                                                {policy.product_name}
                                                                                {policy.premium_amount && (
                                                                                    <span style={{ marginLeft: '4px' }}>
                                                                                        • ₹{Number(policy.premium_amount).toLocaleString('en-IN')}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                                {policies[customer.id].length > 2 && (
                                                                    <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                                                        +{policies[customer.id].length - 2} more
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#999', fontSize: '13px' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td data-label="Status" style={{ textAlign: 'center' }}>
                                                        <span className={`badge ${(customer.status || "active").toLowerCase() === "active" ? "badge-success" : "badge-secondary"}`} style={{ borderRadius: "5px" }}>
                                                            {customer.status || "Active"}
                                                        </span>
                                                    </td>
                                                    <td data-label="Action">
                                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-info" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // Handle view/edit
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <EditOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Edit
                                                            </Button>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-danger" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // Handle delete
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Delete
                                                            </Button>
                                                        </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    renderEmptyState("No customers added yet")
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add Family Member Modal */}
            <Modal show={showFamilyModal} onHide={() => setShowFamilyModal(false)} size="lg" centered style={{ borderRadius: "5px" }}>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '20px 24px', borderRadius: "5px 5px 0 0" }}>
                    <div>
                        <Modal.Title style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                            <UserAddOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                            Add Family Member
                        </Modal.Title>
                        <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, fontWeight: '400' }}>
                            Add a new family member/beneficiary
                        </p>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <form id="family-member-form" onSubmit={handleFamilyFormSubmit}>
                        <div className="row" style={{ rowGap: '20px' }}>
                            {/* Select Customer - Full Width */}
                    <div className="col-12">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Select Customer <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Select
                                        value={familyFormData.customer_id}
                                        onChange={(value) => setFamilyFormData(prev => ({ ...prev, customer_id: value }))}
                                        style={{ width: '100%' }}
                                        size="large"
                                        placeholder="-- Select Customer --"
                                        options={customers.map(customer => ({
                                            value: customer.id,
                                            label: `${customer.name || customer.full_name || customer.customer_name || `Customer #${customer.id}`}${customer.email ? ` - ${customer.email}` : customer.phone ? ` - ${customer.phone}` : ''}`
                                        }))}
                                        className="custom-select"
                                    />
                                    <small style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: '#6b7280' }}>
                                        Select the customer to add a family member for
                                    </small>
                                </div>
                            </div>

                            {/* Full Name */}
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Full Name <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={familyFormData.name}
                                        onChange={handleFamilyFormChange}
                                        placeholder="Enter full name"
                                        required
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    />
                                </div>
                            </div>

                            {/* Relationship */}
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Relationship <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Select
                                        value={familyFormData.relationship}
                                        onChange={(value) => setFamilyFormData(prev => ({ ...prev, relationship: value }))}
                                        style={{ width: '100%' }}
                                        size="large"
                                        placeholder="-- Select Relationship --"
                                        options={RELATION_OPTIONS.map(rel => ({ value: rel, label: rel }))}
                                        className="custom-select"
                                    />
                                </div>
                            </div>

                            {/* Date of Birth */}
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Date of Birth
                                    </label>
                                    <Form.Control
                                        type="date"
                                        name="date_of_birth"
                                        value={familyFormData.date_of_birth}
                                        onChange={handleFamilyFormChange}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    />
                                </div>
                            </div>

                            {/* Phone Number */}
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Phone Number
                                    </label>
                                    <Form.Control
                                        type="tel"
                                        name="phone"
                                        value={familyFormData.phone}
                                        onChange={handleFamilyFormChange}
                                        placeholder="Enter phone number"
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Email
                                    </label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={familyFormData.email}
                                        onChange={handleFamilyFormChange}
                                        placeholder="Enter email address"
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    />
                                </div>
                            </div>

                            {/* Address */}
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Address
                                    </label>
                                    <Form.Control
                                        type="text"
                                        name="address"
                                        value={familyFormData.address}
                                        onChange={handleFamilyFormChange}
                                        placeholder="Enter address"
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    />
                                </div>
                            </div>

                            {/* Notes - Full Width */}
                            <div className="col-12">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                        Notes (Optional)
                                    </label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="notes"
                                        value={familyFormData.notes}
                                        onChange={handleFamilyFormChange}
                                        placeholder="Additional information about this family member"
                                        style={{ resize: 'vertical', borderRadius: "5px" }}
                                    />
                        </div>
                    </div>
                </div>
                    </form>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '16px 24px', borderRadius: "0 0 5px 5px" }}>
                    <Button 
                        onClick={() => {
                            setShowFamilyModal(false);
                            setFamilyFormData({
                                customer_id: "",
                                name: "",
                                email: "",
                                phone: "",
                                relationship: "",
                                date_of_birth: "",
                                gender: "",
                                address: "",
                                notes: "",
                            });
                        }}
                        style={{ borderRadius: "5px" }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        htmlType="submit"
                        form="family-member-form"
                        loading={submittingFamily}
                        style={{ minWidth: '140px', borderRadius: "5px" }}
                    >
                        Add Family Member
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CustomerManagement;

